/**
 * **The shell.** A third, separate element that flies the path from the chat
 * surface to the Fragen panel — `position: fixed`, portalled to `document.body`.
 *
 * Why not `layoutId` (the state up to A10): motion would then have to connect
 * two rects in two different containers (the `<Outlet>` with `key={pathname}`
 * versus the sibling `aside`), and the `layoutId` sat on the **scrolling** chat
 * column — any non-uniform size change distorts the children there while the
 * animation runs. Instead, a dedicated box flies here, starting at the measured
 * start rect and ending at the measured target rect.
 *
 * Three rules that carry the whole design:
 *
 * 1. **No `scale`.** The shell animates `x/y` (transform) and `width/height`
 *    (layout). A non-uniform scale (here ≈ 0.44 × 1.08) could not be inverted
 *    cleanly on the children: text would be re-rasterized when counter-scaled
 *    (shimmer), borders and radii would turn oval.
 * 2. **Fixed pixel widths in the layers.** The shell has only two absolutely
 *    positioned children of fixed size; its size animation therefore triggers
 *    no reflow of the content, only a paint of the shell surface. The children
 *    animate `opacity` exclusively (compositor). Text is clipped, never
 *    squashed.
 * 3. **No `border`, no `box-shadow` on the moving box.** Both fray under a ~3x
 *    horizontal squash. The shadow is its own layer (`opacity` only), the
 *    panel's hairline its own overlay that only fades in shortly before
 *    docking.
 *
 * **Polish of 22.09.2026** (product owner's brief: „richtig clean und smooth
 * animiert, ohne dass es zuviel Schatten ist" — really clean and smoothly
 * animated, without too much shadow). Removed: the scrim over the target page —
 * a full-screen black layer that darkened half the application on every click
 * and suggested an effort the operation does not have. The remaining shadow is
 * the faintest one that still separates the surface from the background
 * (`MORPH_SHADOW`), and the flight runs on **one** curve without overshoot
 * instead of a spring with `bounce`. The layers swap in a symmetric crossfade
 * around the middle of the flight, not in two staggered tracks.
 *
 * Common anchor: both layers are anchored at the **bottom**. The input row is
 * the element both worlds share and therefore sticks to the shell's bottom edge
 * during the whole flight — transcript on top, input at the bottom, in both
 * views. That is why it feels like *one* element and not like two crossfaded
 * pictures.
 *
 * **Clipping and shadow are separate.** The shadow is a direct, *unclipped*
 * child of the shell; everything that must be clipped (layer A, layer B,
 * hairline) sits in its own clipper below it. If `overflow: hidden` (or
 * `contain: paint`) were on the shell itself, it would cut away its own outer
 * shadow entirely — the card would never lift off.
 *
 * **`chat-oa` belongs to layer A, not to the shell.** `AppShell` removes the
 * class from `body` in the same commit, and `chat-oa.css` works with
 * descendant selectors (`.chat-oa .markdown a`, `.chat-oa .chatBot
 * .messagesContainer`) — without it the clone's colours and scrollbars flip in
 * the first frame. On the whole shell, though, it would also apply to layer B,
 * and the panel preview should look like the real panel, which does **not**
 * carry the class. Otherwise the crossfade would blend link colours and heading
 * sizes against each other instead of two identical pictures.
 *
 * **All tracks start with the flight**, not with mounting: the curve only
 * starts once the target rect is known (usually in the same layout effect, in
 * the error case up to `MORPH_TARGET_MEASURE_MS` later). If layer A and the
 * shadow ran before that, an empty, motionless box would be on screen —
 * exactly the white frame the design rules out.
 */

import { Component, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "motion/react";

import {
    beginMorphHandover,
    cancelAssistantMorph,
    markMorphDone,
    type AssistantMorph,
} from "./assistantMorph";
import { ghostScroller, type MorphRect } from "./assistantSurface";
import AssistantPanelPreview from "./AssistantPanelPreview";
import {
    MORPH_CANCEL_FADE_MS,
    MORPH_FLIGHT_TRANSITION,
    MORPH_HANDOVER_MS,
    MORPH_MAX_MS,
    MORPH_SHADOW,
    MORPH_TARGET_MEASURE_MS,
    SPAN_CROSSFADE,
    SPAN_HAIRLINE_IN,
    SPAN_SHADOW_IN,
    SPAN_SHADOW_OUT,
    handoverStartMs,
    pulseKeyframes,
    radiusKeyframes,
    spanTransition,
    toSeconds,
} from "./morphTimeline";

/** Above Sheet/Dialog (`z-[10050]`) and Popover/Select (`z-[10100]`) — see docs. */
const Z_HULL = 10200;

function readRect(node: HTMLElement | null): MorphRect | null {
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

/**
 * Layer B renders a foreign snapshot (the main chat's conversation) through
 * the full Markdown renderer. If something throws there, the next boundary
 * would be the `GlobalErrorBoundary` in `src/index.tsx` — an animation detail
 * would then take down the whole application. Here only layer B fails instead;
 * the crossfade then fades to the real panel, which loads the same history
 * itself anyway.
 */
class PreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

interface Properties {
    morph: AssistantMorph;
    /** The real `aside` — its rect is the flight's target. */
    targetRef: { current: HTMLElement | null };
}

export default function AssistantMorphOverlay({ morph, targetRef }: Properties) {
    const [target, setTarget] = useState<MorphRect | null>(null);
    const hostA = useRef<HTMLDivElement>(null);
    /**
     * When the flight started. All deadlines hang off this and **not** off
     * `morph.startedAt`: between the click and the start of the flight lies the
     * measurement of the target rect (normally 0 ms, in the error case up to
     * 250 ms). If the deadlines counted from the click, the handover would blow
     * the shell away mid-flight.
     */
    const flightStartedAtRef = useRef<number | null>(null);

    const origin = morph.originRect;
    const surface = morph.surfaceRect;

    /**
     * The target rect. The real `aside` is mounted in the same commit as the
     * chat teardown (`AppShell` derives `assistantVisible` **during render**) —
     * so the rect is usually available immediately. If not yet, two
     * `requestAnimationFrame`s and a `ResizeObserver`, deadline
     * `MORPH_TARGET_MEASURE_MS`; after that cancel, the panel fades in.
     */
    useLayoutEffect(() => {
        let done = false;
        const finish = (rect: MorphRect) => {
            if (done) return;
            done = true;
            flightStartedAtRef.current = Date.now();
            setTarget(rect);
        };

        const immediate = readRect(targetRef.current);
        if (immediate) {
            finish(immediate);
            return;
        }

        const attempt = () => {
            const rect = readRect(targetRef.current);
            if (rect) finish(rect);
        };

        let second = 0;
        const first = window.requestAnimationFrame(() => {
            attempt();
            second = window.requestAnimationFrame(attempt);
        });

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined" && targetRef.current) {
            observer = new ResizeObserver(attempt);
            observer.observe(targetRef.current);
        }

        const deadline = window.setTimeout(() => {
            if (!done) cancelAssistantMorph();
        }, MORPH_TARGET_MEASURE_MS);

        return () => {
            done = true;
            window.cancelAnimationFrame(first);
            if (second) window.cancelAnimationFrame(second);
            observer?.disconnect();
            window.clearTimeout(deadline);
        };
    }, [morph.id, targetRef]);

    /**
     * Layer A: the clone of the chat surface is attached once, the scroll
     * position written back by hand (a freshly cloned node reports 0). The
     * `parentElement` check makes the double run under `<React.StrictMode>`
     * harmless.
     */
    useLayoutEffect(() => {
        const host = hostA.current;
        const ghost = morph.ghost;
        if (!host || ghost.parentElement === host) return;
        host.appendChild(ghost);
        const scroller = ghostScroller(ghost);
        if (scroller) scroller.scrollTop = morph.scrollTop;
    }, [morph.ghost, morph.id, morph.scrollTop]);

    /**
     * Handover: after the flight, but not before `panelReady` — capped, and
     * measured from the **start of the flight**. `panelReadyMs` does count from
     * the click, but via `handoverStartMs` it always lies after the end of the
     * flight; so the shell is never faded out before docking.
     */
    useEffect(() => {
        if (!target || morph.phase !== "flying") return;
        const started = flightStartedAtRef.current ?? morph.startedAt;
        const elapsed = Date.now() - started;
        const delay = Math.max(0, handoverStartMs(morph.panelReadyMs) - elapsed);
        const id = morph.id;
        const timer = window.setTimeout(() => beginMorphHandover(id), delay);
        return () => window.clearTimeout(timer);
    }, [morph.id, morph.panelReadyMs, morph.phase, morph.startedAt, target]);

    /** End of the crossfade — the shell disappears, the real panel stands. */
    useEffect(() => {
        if (morph.phase !== "handover") return;
        const id = morph.id;
        const timer = window.setTimeout(() => markMorphDone(id), MORPH_HANDOVER_MS);
        return () => window.clearTimeout(timer);
    }, [morph.id, morph.phase]);

    /**
     * The watchdog. Animation callbacks **never** fire for a cancelled animation
     * or in jsdom — this is the only guaranteed way back to "no morph". As long
     * as it runs, the panel cannot get stuck invisible. It deliberately counts
     * from the click (`startedAt`), not from the start of the flight: it is the
     * net under *everything*, including a hanging measurement.
     */
    useEffect(() => {
        const id = morph.id;
        const remaining = Math.max(0, MORPH_MAX_MS - (Date.now() - morph.startedAt));
        const timer = window.setTimeout(() => markMorphDone(id), remaining);
        return () => window.clearTimeout(timer);
    }, [morph.id, morph.startedAt]);

    /** A resized window invalidates the target rect; a hidden tab the whole animation. */
    useEffect(() => {
        const abort = () => cancelAssistantMorph();
        const onVisibility = () => {
            if (document.visibilityState === "hidden") abort();
        };
        window.addEventListener("resize", abort);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("resize", abort);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [morph.id]);

    const geometry = target ?? origin;
    const radius = radiusKeyframes();
    const shadow = pulseKeyframes(SPAN_SHADOW_IN, SPAN_SHADOW_OUT);
    /** Layer A out, layer B in — the **same** track, see `SPAN_CROSSFADE`. */
    const crossfade = spanTransition(SPAN_CROSSFADE);
    const handingOver = morph.phase === "handover";
    const cancelled = morph.phase === "cancelled";
    /**
     * The flight is running. Once true, stays true: `target` never falls back to
     * `null` — otherwise the tracks would run backwards if a cancel intervenes.
     */
    const started = target !== null;
    const cancelFade = { duration: toSeconds(MORPH_CANCEL_FADE_MS), ease: "linear" as const };

    return (
        <>
            {/* No scrim over the target page: it darkened half the application
                on every click, and the target page fades in by itself anyway
                (`guide-morph-page-enter`). No `filter`/`backdrop-filter` —
                that would be the most expensive effect imaginable and the
                surest way below 60 fps. */}
            <motion.div
                data-testid="assistant-morph-hull"
                aria-hidden="true"
                // React 18 does not know the `inert` prop — spread as a string
                // it lands in the DOM as an attribute. The cloned layer A
                // contains a real `<textarea>` and could otherwise catch focus.
                {...{ inert: "" }}
                className="pointer-events-none fixed"
                style={{
                    zIndex: Z_HULL,
                    left: origin.left,
                    top: origin.top,
                    background: morph.fill,
                    // No `paint`: that would cut away the outer shadow. Clipping
                    // happens in the clipper below.
                    contain: "layout style",
                    willChange: "transform, width, height",
                }}
                initial={{
                    x: 0,
                    y: 0,
                    width: origin.width,
                    height: origin.height,
                    borderRadius: 0,
                    opacity: 1,
                }}
                animate={{
                    x: geometry.left - origin.left,
                    y: geometry.top - origin.top,
                    width: geometry.width,
                    height: geometry.height,
                    borderRadius: started ? radius.values : 0,
                    opacity: handingOver || cancelled ? 0 : 1,
                }}
                transition={{
                    default: MORPH_FLIGHT_TRANSITION,
                    borderRadius: {
                        duration: radius.duration,
                        times: radius.times,
                        ease: radius.ease,
                    },
                    opacity: cancelled
                        ? cancelFade
                        : { duration: toSeconds(MORPH_HANDOVER_MS), ease: "linear" },
                }}
            >
                {/* Shadow layer — its own element so no `box-shadow` is
                    interpolated per frame, and **outside** the clipper,
                    because an outer shadow is by definition painted beyond its
                    own border box. Exactly **one** shadow, and a very faint one
                    (`MORPH_SHADOW`): the surface should stand out from the
                    background, not float above it. */}
                <motion.div
                    data-testid="assistant-morph-shadow"
                    className="pointer-events-none absolute inset-0 rounded-[inherit]"
                    style={{ boxShadow: MORPH_SHADOW }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: started ? shadow.values : 0 }}
                    transition={{
                        duration: shadow.duration,
                        times: shadow.times,
                        ease: shadow.ease,
                    }}
                />

                {/* The clipper: everything visual is clipped here so the shell
                    itself can do without `overflow: hidden`. */}
                <div
                    data-testid="assistant-morph-clip"
                    className="absolute inset-0 overflow-hidden rounded-[inherit]"
                >
                    {/* Layer A — the clone of the chat surface, anchored bottom
                        and left. `chat-oa` here and not on the shell (otherwise
                        the chat scope would also tint layer B); `.chatBot` as an
                        intermediate level because the legacy selectors from
                        `App.sass`/`chat-oa.css` expect that parent — and as a
                        **flex container** so the clone stays a flex child. */}
                    <motion.div
                        data-testid="assistant-morph-layer-a"
                        className="chat-oa absolute"
                        style={{
                            width: surface.width,
                            height: surface.height,
                            left: surface.left - origin.left,
                            bottom: origin.top + origin.height - (surface.top + surface.height),
                        }}
                        initial={{ opacity: 1 }}
                        animate={{ opacity: started ? 0 : 1 }}
                        transition={crossfade}
                    >
                        <div ref={hostA} className="chatBot flex h-full w-full" />
                    </motion.div>

                    {/* Layer B — the panel preview at target width, anchored
                        bottom and right. Only once the target is measured:
                        before that its width would be a guess. The transparent
                        left border compensates for the `border-l` of the real
                        `aside` — with `box-sizing: border-box` its content box
                        is 1 px narrower than `target.width`, and without the
                        compensation the whole panel content would shift by one
                        pixel during the crossfade. */}
                    {target ? (
                        <motion.div
                            data-testid="assistant-morph-layer-b"
                            className="absolute bottom-0 right-0"
                            style={{
                                width: target.width,
                                height: target.height,
                                borderLeft: "1px solid transparent",
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={crossfade}
                        >
                            <PreviewBoundary>
                                <AssistantPanelPreview snapshot={morph.snapshot} />
                            </PreviewBoundary>
                        </motion.div>
                    ) : null}

                    {/* The panel's hairline (the `aside`'s `border-l`) — its own
                        overlay that does not fly along, fades in only on docking. */}
                    <motion.div
                        className="absolute inset-y-0 left-0 w-px bg-border"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: started ? 1 : 0 }}
                        transition={spanTransition(SPAN_HAIRLINE_IN)}
                    />
                </div>
            </motion.div>
        </>
    );
}
